import { useEffect, useRef } from 'react';
import type * as Y from 'yjs';
import { useDiagramPresentationScene } from '../../../hooks/useDiagramPresentationScene';
import { composeProjectedScene } from '../../../core/canvas/composeProjectedScene';
import type { Scene } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useOrgModel } from './useOrgModel';
import {
  buildPositionChartScene,
  positionChartSceneNodeUpdates,
  positionChartTopologyMatches,
} from '../services/buildPositionChartScene';
import { orgProjectionCompletenessError } from '../services/orgProjectionCompleteness';
import { usePositionChartGrouping } from './usePositionChartGrouping';
import { useOptimisticPositionStructureModel } from './useOptimisticPositionStructureModel';
import { useOrgPlanningScenarioProjection } from './useOrgPlanningScenarioProjection';
import { subscribeProtectedPlanningInvalidation } from './protectedPlanningGeneration';
import { isAtlasPositionProjection } from '../services/atlasProjectionContext';
import { mayRetainOrgProjectionScene, mayShowCurrentOrgContext } from './orgProjectionContinuity';

/** Hydrates a Position Chart marker from the permissioned org-model projection. */
export function usePositionChartSync(diagramId: string | undefined, doc?: Y.Doc | null): {
  isPositionChart: boolean;
  isLoading: boolean;
  hasProjectionData: boolean;
  isShowingLiveContext: boolean;
  error: unknown;
  folderId: string | null;
  model: ReturnType<typeof useOrgModel>['data'];
  refetch: ReturnType<typeof useOrgModel>['refetch'];
} {
  const { data: diagram } = useDiagramInfo(diagramId);
  const isPositionChart = isAtlasPositionProjection(diagram?.viewDefinition);
  const presentationScene = useDiagramPresentationScene(doc, isPositionChart);
  const previousPresentation = useRef<typeof presentationScene | null>(null);
  const sourceFolderId = diagram?.viewDefinition?.sourceFolderId ?? diagram?.folderId;
  const orgModel = useOrgModel({ folderId: sourceFolderId, diagramId, enabled: isPositionChart });
  const scenarioProjection = useOrgPlanningScenarioProjection({
    folderId: sourceFolderId, diagramId, enabled: isPositionChart, projectionKind: 'position',
  });
  const grouping = usePositionChartGrouping(
    isPositionChart ? sourceFolderId : null,
    isPositionChart ? diagram?.id : null,
    isPositionChart ? diagram?.viewDefinition : null,
  );
  const loadScene = useDiagramStore((state) => state.loadScene);
  const updateNodes = useDiagramStore((state) => state.updateNodes);
  const setInitialLoading = useDiagramStore((state) => state.setInitialLoading);
  const previousScene = useRef<Scene | undefined>(undefined);
  const previousSceneFolderId = useRef<string | null>(null);
  const folderId = sourceFolderId ?? null;
  const isShowingLiveContext = mayShowCurrentOrgContext({
    selected: scenarioProjection.hasSelectedScenario,
    scenarioError: scenarioProjection.error,
    discoveryError: scenarioProjection.discoveryError,
    currentError: orgModel.error,
    currentModel: orgModel.data,
  });
  const effectiveModel = scenarioProjection.hasSelectedScenario && !isShowingLiveContext
    ? scenarioProjection.data?.model : orgModel.data;
  const planningChart = diagram?.viewDefinition?.projection === 'planning_chart';
  const projectionError = orgModel.error
    ?? orgProjectionCompletenessError(orgModel.data)
    ?? (scenarioProjection.hasSelectedScenario || planningChart ? scenarioProjection.discoveryError : null)
    ?? orgProjectionCompletenessError(effectiveModel) ?? scenarioProjection.error;
  // Query already retains data during ordinary background refresh. A reset
  // or denial must revoke the view, never fall back to an unleased ref.
  const canonicalModel = (projectionError && !isShowingLiveContext) || (planningChart && scenarioProjection.isDiscovering) ? undefined : effectiveModel;
  const optimisticModel = useOptimisticPositionStructureModel(folderId, isShowingLiveContext ? undefined : canonicalModel);
  const model = isShowingLiveContext ? canonicalModel : optimisticModel;
  const retainedSceneAvailable = mayRetainOrgProjectionScene({
    hasScene: Boolean(previousScene.current),
    sameScope: previousSceneFolderId.current === folderId,
    error: projectionError,
  });

  useEffect(() => {
    if (!isPositionChart) return;
    return subscribeProtectedPlanningInvalidation((invalidatedFolder) => {
      if (invalidatedFolder && invalidatedFolder !== folderId) return;
      previousScene.current = undefined;
      previousSceneFolderId.current = null;
      loadScene(composeProjectedScene({ version: 1, nodes: [], edges: [] }, presentationScene));
    });
  }, [folderId, isPositionChart, loadScene, presentationScene]);

  useEffect(() => {
    if (!isPositionChart) return;
    if (!model || !folderId) {
      if (mayRetainOrgProjectionScene({
        hasScene: Boolean(previousScene.current),
        sameScope: previousSceneFolderId.current === folderId,
        error: projectionError,
      })) return;
      previousScene.current = undefined;
      previousSceneFolderId.current = null;
      loadScene(composeProjectedScene({ version: 1, nodes: [], edges: [] }, presentationScene));
      return;
    }
    if (previousSceneFolderId.current !== folderId) {
      previousScene.current = undefined;
      previousSceneFolderId.current = folderId;
    }
    const previous = previousScene.current;
    // Position Chart must open with the same tree geometry as the visible
    // Auto Layout action. The canonical tree layout is linear and fast at
    // this projection size; routing first paint through ELK produced a
    // materially different, extremely wide chart.
    const projected = buildPositionChartScene(model, previous, {
      groupingPolicy: grouping.policy,
    });
    const annotationsChanged = previousPresentation.current !== presentationScene;
    previousPresentation.current = presentationScene;
    if (previous && presentationScene.projectedLayout?.mode !== 'custom' && !annotationsChanged
      && positionChartTopologyMatches(previous, projected.nodes, projected.edges)) {
      previousScene.current = projected;
      const updates = positionChartSceneNodeUpdates(previous, projected);
      if (updates.length > 0) updateNodes(updates, true);
      setInitialLoading(false);
      return;
    }
    previousScene.current = projected;
    loadScene(composeProjectedScene(projected, presentationScene), { preserveSelection: true });
    setInitialLoading(false);
  }, [folderId, grouping.policy, isPositionChart, loadScene, model, presentationScene, projectionError, setInitialLoading, updateNodes]);

  useEffect(() => {
    if (!isPositionChart) {
      previousScene.current = undefined;
      previousSceneFolderId.current = null;
    }
  }, [isPositionChart]);

  return {
    isPositionChart: Boolean(isPositionChart),
    isLoading: Boolean(isPositionChart && !projectionError && !model && !retainedSceneAvailable),
    hasProjectionData: Boolean(model || retainedSceneAvailable),
    isShowingLiveContext,
    error: projectionError,
    folderId,
    model,
    refetch: orgModel.refetch,
  };
}
